import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline'
import React, { Fragment,  useEffect, useState } from 'react'
import { Control, Controller } from 'react-hook-form'
import { ErrorText, Label } from 'components/InputLabel/styles'

import {
    boxButton,
    boxOptions,
    IconCheck,
    IconDown,
    listBoxOption,
    Main,
    SelectSearchInput,
    Span,
    WrapperButton
} from 'components/InputSelect/styles'
import { IoCloseSharp } from 'react-icons/io5'
import { separateStrings } from 'helper/getMatchingStringWithSearch'


export interface ISelectProps {
    ref?: React.MutableRefObject<any>
    icon?: JSX.Element
    errorTextStyle?: string
    styleContainer?: string
    styleBoxOptions?: string
    data?: any[]
    placeholder?: string
    errorStyle?: string
    disabled?: boolean
    iconColorDisabled?: boolean
    control: Control<any, any>
    name: string
    label?: string
    defaultValue?: any
    required?: boolean,
    onChange?: any
    onBlur?: any
    shouldHaveSearch?: boolean,
    multiple?: boolean
    allIsSelected?: boolean
    selectAll?: ({ name, options, isAllSelected }: { name: string, options: any[], isAllSelected: boolean }) => void
    unitText?: string
    searchPlaceHolder?: string
    multiUnitText?: string
    onMount?: () => void
    onFocus?: () => void
    canSelect?: (a: any) => boolean
    hideSelectAll?: boolean
}

const InputSelect: React.FC<ISelectProps> = ({
    ref,
    onFocus,
    icon,
    disabled,
    styleContainer,
    iconColorDisabled,
    data,
    placeholder,
    control,
    name,
    label,
    styleBoxOptions,
    defaultValue,
    required = false,
    onChange: onChangeMain,
    onBlur: onBlurMain,
    errorStyle,
    errorTextStyle,
    shouldHaveSearch,
    multiple,
    selectAll,
    allIsSelected,
    unitText,
    multiUnitText,
    searchPlaceHolder,
    onMount,
    canSelect,
    hideSelectAll
}) => {
    const [isAllSelected, setIsAllSelected] = useState(allIsSelected)
    const [options, setOptions] = useState([])
    const [optionsSearch, setOptionsSearch] = useState('')
    const [listIsClosed, setListIsClosed] = useState(true)
    const [loadedOptions, setLoadedOptions] = useState(0);

    useEffect(() => {
        setOptions(data)
    }, [data])

    useEffect(() => {
        if (onMount) {
            onMount()
        }
    }, [])

    useEffect(() => {
        if (!listIsClosed) {
            if (options?.length > 100) {
                const perRender = 50
                let iterationCount = 0;
                const totalIterations = Math.ceil(options?.length / perRender);
                const interval = setInterval(() => {
                    if (iterationCount < totalIterations) {
                        setLoadedOptions(prev => {
                            return prev + perRender >= options?.length ? options?.length : prev + perRender
                        })
                        iterationCount++
                    } else {
                        clearInterval(interval)
                    }
                }, 5)
            } else {
                setLoadedOptions(options?.length)
            }
        }
    }, [options, listIsClosed])

    useEffect(() => {
        const searchData = data?.filter(option => {
            return option?.label?.toLowerCase().includes(optionsSearch.toLowerCase())
        })
        setOptions(searchData)
    }, [optionsSearch])

    useEffect(() => {
        if (selectAll && !hideSelectAll) {
            selectAll({ name, options: options, isAllSelected: !allIsSelected })
        }
    }, [])

    return (
        <Controller
            defaultValue={defaultValue}
            control={control}
            rules={{ required: required }}
            name={name}
            render={({ field: { onBlur, onChange, value } }) => {
                const allOptionsIsSelected = value?.length === options?.length
                return <Listbox
                    ref={ref}
                    data-testid="test-select-input"
                    disabled={disabled}
                    value={value}
                    onChange={(onChangeMain || onChange)}
                    name={name}
                    multiple={multiple}
                >
                    {({ open }) => {
                        if (!open) {
                            setListIsClosed(true)
                            setLoadedOptions(0)
                        } else if (open) {
                            setListIsClosed(false)
                        }
                        return (
                            <Main data-field-container={name}
                            >
                            
                                <div className="min-w-max">
                                    {label && <Label>{label}</Label>}
                                </div>

                                <WrapperButton
                                    className={`${errorStyle} ${styleContainer}`}
                                >
                                    {icon && <span data-testid="test-select-icon">{icon}</span>}
                                    <Listbox.Button
                                        onFocus={onFocus}
                                        name={name}
                                        className={boxButton} onBlur={(onBlurMain || onBlur)} >
                                        <Span
                                            className={`${iconColorDisabled
                                                ? 'text-gray-300'
                                                : 'text-textIcon'
                                                }`}
                                        >

                                            {isAllSelected ?
                                                <span className="text-darkGray/80">
                                                    {placeholder}
                                                </span>
                                                :
                                                value?.length >= 1 && multiple && multiUnitText && unitText ? 
                                                <span className="text-darkGray/80">
                                                    {`${value?.length} ${value?.length >= 2 ? multiUnitText : unitText}`}
                                                </span> 
                                                : 
                                                value ? (
                                                <span title={options?.find((e) => e?.value?.length > 30 && e?.value === value)?.label}>
                                                    {options?.find((e) => e?.value === value)?.label }
                                                </span>
                                                ) : (
                                                <span className="text-darkGray/80">
                                                    {placeholder}
                                                </span>
                                                )}
                                        </Span>
                                        <IconDown>
                                            <ChevronDownIcon
                                                className={`h-5 w-5 text-blueTheme`}
                                                aria-hidden="true"
                                            />
                                        </IconDown>
                                    </Listbox.Button>
                                    <Transition
                                        as={Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <Listbox.Options
                                            className={`${boxOptions} ${styleBoxOptions}`}>
                                            {
                                                shouldHaveSearch ?
                                                    <div className='w-[100%] p-3 sticky top-[-4px] bg-white z-[10]' >
                                                        <SelectSearchInput
                                                            className='w-[100%] p-3 border-2 border-blueTheme' type="text"
                                                            placeholder={searchPlaceHolder}
                                                            data-testid="test-select-search-component"
                                                            onKeyDown={(e) => {
                                                                const allowedHeadlessUIKeys = [
                                                                    "ArrowUp",
                                                                    "ArrowDown",
                                                                    "Enter",
                                                                    "Home",
                                                                    "End",
                                                                    "Escape",
                                                                ];
                                                                if (!allowedHeadlessUIKeys.includes(e.key)) {
                                                                    e.stopPropagation();
                                                                }
                                                            }}
                                                            value={optionsSearch}
                                                            onChange={(e) => {
                                                                setOptionsSearch(e.target.value)
                                                            }}
                                                        />
                                                        {
                                                            optionsSearch ?
                                                                <IoCloseSharp
                                                                    data-testid="clean-icon"
                                                                    onClick={() => {
                                                                        setOptionsSearch("")
                                                                    }}
                                                                    className="text-blueTheme absolute right-2 top-[20px] mr-[13.5px] w-5 h-5"
                                                                /> : null
                                                        }
                                                    </div>
                                                    : null
                                            }
                                            {
                                                multiple && !hideSelectAll ?
                                                    <Listbox.Option
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            if (selectAll) {
                                                                selectAll({ name, options, isAllSelected })
                                                            }
                                                            setIsAllSelected(prev => !prev)
                                                        }}
                                                        className={boxButton}
                                                        key={0}
                                                        value={''}>
                                                        {({ selected }) => (
                                                            <>
                                                                <Span
                                                                    className={`block truncate ${isAllSelected || allOptionsIsSelected
                                                                        ? 'pl-5 font-medium'
                                                                        : 'font-normal'
                                                                        }`}
                                                                >
                                                                    <span className="text-darkGray/80 pl-5 font-medium">
                                                                        {placeholder}
                                                                    </span>
                                                                    {isAllSelected || allOptionsIsSelected ? (
                                                                        <IconCheck>
                                                                            <CheckIcon
                                                                                className="h-5 w-5"
                                                                                aria-hidden="true"
                                                                            />
                                                                        </IconCheck>
                                                                    ) : null}
                                                                </Span>
                                                            </>
                                                        )}


                                                    </Listbox.Option>
                                                    : !multiple ?
                                                    <Listbox.Option className={boxButton}
                                                        key={0}
                                                        value={''}>

                                                        <Span
                                                            className={`${iconColorDisabled
                                                                ? 'text-gray-300'
                                                                : 'text-textIcon'
                                                                }`}
                                                        >
                                                            <span className="text-darkGray/80 pl-5 font-medium">
                                                                {placeholder}
                                                            </span>
                                                        </Span>
                                                    </Listbox.Option>
                                                    : null
                                            }
                                            {options?.slice(0, loadedOptions)?.map((item, index) => {
                                                return <Listbox.Option
                                                    onClick={() => {
                                                        setIsAllSelected(false)
                                                    }}
                                                    disabled={canSelect ? canSelect(item.value) : disabled}
                                                    key={index + 1}
                                                    className={({ active }) =>
                                                        `${listBoxOption} ${active
                                                            ? 'text-amber-400 bg-blue-100'
                                                            : ''
                                                        }`
                                                    }
                                                    value={
                                                        item.value || item.label || item
                                                    }
                                                >
                                                    {({ selected }) => {
                                                        const text = item.label || item.value || item
                                                        const parts = separateStrings(text, optionsSearch, item)
                                                        return (
                                                            <>
                                                                {/* {parts} */}
                                                                <div title={text?.length > 30 ? text : ''}
                                                                    data-testid="test-option-container"
                                                                    className={`block truncate ${selected
                                                                        ? 'pl-5 font-medium'
                                                                        : 'font-normal'
                                                                        }`}
                                                                >
                                                                    {parts?.map((part, index) => (
                                                                        part?.toLowerCase() === optionsSearch.toLowerCase() ?
                                                                            <b
                                                                                key={index}
                                                                                className={`${item.value !==
                                                                                    placeholder &&
                                                                                    ''
                                                                                    } text-[16px]`}
                                                                            >
                                                                                {part}
                                                                            </b> :
                                                                            <span
                                                                                key={index}
                                                                                className={`${item.value !==
                                                                                    placeholder &&
                                                                                    ''
                                                                                    } text-[16px]`}
                                                                            >
                                                                                {part}
                                                                            </span>
                                                                    ))}
                                                                </div>
                                                                {selected ? (
                                                                    <IconCheck data-testid="test-option-selected">
                                                                        <CheckIcon
                                                                            className="h-5 w-5"
                                                                            aria-hidden="true"
                                                                        />
                                                                    </IconCheck>
                                                                ) : null}
                                                            </>
                                                        )
                                                    }}
                                                </Listbox.Option>
                                            }) || []}
                                        </Listbox.Options>
                                    </Transition>
                                </WrapperButton>
                                <ErrorText className={`${errorTextStyle}`}>
                                    {errorStyle && 'Preenchimento obrigatório.'}
                                </ErrorText>
                            </Main>
                        )
                    }}

                </Listbox>
            }}
        />
    )
}


export { InputSelect }